import { useEffect, useState } from "react";
import { Activity, Database, Server, Shield, Zap, Network, Layers } from "lucide-react";
import { getHealth, getHealthDeep, type HealthPing, type HealthDeep } from "../services/health";
import { PageHeader } from "../components/layout/PageHeader";
import { JsonViewer } from "../components/saga/JsonViewer";
import { cn } from "../utils/cn";

export default function HealthCheck() {
  const [ping, setPing] = useState<HealthPing | null>(null);
  const [deep, setDeep] = useState<HealthDeep | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const p = await getHealth();
        if (alive) setPing(p);
        const d = await getHealthDeep();
        if (alive) setDeep(d);
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : "Falha ao consultar healthcheck");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const backendOk = ping?.status === "ok";
  const fmtLatency = (ms: number | null | undefined) =>
    ms === null || ms === undefined ? "Sem dados" : `latency ${ms}ms`;

  return (
    <>
      <PageHeader title="Healthcheck" subtitle="Status do backend e dependências" />

      <div className="p-6 space-y-6">
        {loading && <p className="text-sm text-muted-foreground">Consultando backend…</p>}
        {err && <div className="p-3 rounded-md bg-danger-soft text-danger-soft-fg text-sm">{err}</div>}

        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
          <CheckCard
            icon={Server}
            label="Backend"
            ok={backendOk}
            primary={ping?.service ?? "—"}
            details={ping ? `versão ${ping.version}` : "Sem dados"}
          />
          <CheckCard
            icon={Database}
            label="PostgreSQL"
            ok={deep?.checks.database.ok ?? false}
            primary={deep?.checks.database.ok ? "ok" : (deep ? "down" : "—")}
            details={fmtLatency(deep?.checks.database.latencyMs)}
          />
          <CheckCard
            icon={Layers}
            label="Redis (BullMQ)"
            ok={deep?.checks.redis.ok ?? false}
            primary={deep?.checks.redis.ok ? "ok" : (deep ? "down" : "—")}
            details={fmtLatency(deep?.checks.redis.latencyMs)}
          />
          <CheckCard
            icon={Shield}
            label="Keycloak"
            ok={deep?.checks.keycloak.ok ?? false}
            primary={deep?.checks.keycloak.ok ? "ok" : (deep ? "down" : "—")}
            details={fmtLatency(deep?.checks.keycloak.latencyMs)}
          />
          {deep?.checks.idp ? (
            <CheckCard
              icon={Network}
              label="Wynk IDP"
              ok={deep.checks.idp.ok}
              primary={deep.checks.idp.ok ? "ok" : "down"}
              details={fmtLatency(deep.checks.idp.latencyMs)}
            />
          ) : (
            <CheckCard
              icon={Network}
              label="Wynk IDP"
              ok={true}
              primary="not-required"
              details="Backend em modo stub (IDP_USE_HTTP=false + WYNK_AUTH_ENABLED=false)"
            />
          )}
        </div>

        {ping && (
          <section>
            <h2 className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2 flex items-center gap-1.5">
              <Activity size={12} /> GET /api/health
            </h2>
            <JsonViewer value={ping} />
          </section>
        )}

        {deep && (
          <section>
            <h2 className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2 flex items-center gap-1.5">
              <Zap size={12} /> GET /api/health/deep
            </h2>
            <JsonViewer value={deep} />
          </section>
        )}
      </div>
    </>
  );
}

interface CheckCardProps {
  icon: typeof Server;
  label: string;
  ok: boolean;
  primary: string;
  details: string;
}

function CheckCard({ icon: Icon, label, ok, primary, details }: CheckCardProps) {
  return (
    <div className={cn(
      "rounded-md border p-4 flex items-start gap-3",
      ok ? "border-success/30 bg-success-soft/30" : "border-danger/30 bg-danger-soft/30",
    )}>
      <div className={cn("p-2 rounded", ok ? "bg-success/15 text-success" : "bg-danger/15 text-danger")}>
        <Icon size={18} strokeWidth={1.75} />
      </div>
      <div className="flex-1">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
        <p className="text-sm font-medium mt-0.5 font-mono-tight">{primary}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{details}</p>
      </div>
      <span className={cn(
        "inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-xs font-medium font-mono-tight",
        ok ? "bg-success-soft text-success-soft-fg border-success/20" : "bg-danger-soft text-danger-soft-fg border-danger/20",
      )}>
        <span className={cn("w-1.5 h-1.5 rounded-full", ok ? "bg-success" : "bg-danger")} aria-hidden="true" />
        {ok ? "ok" : "down"}
      </span>
    </div>
  );
}
